import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { BACKEND_URL } from '../config';
import { useOrganization } from '../context/OrganizationContext';
import { fetchWithOrg } from '../utils/api';
import WorkflowPreviewModal from '../components/WorkflowPreviewModal';

import {
  ArrowLeft,
  CircleNotch,
  Play
} from '@phosphor-icons/react';
/**
 * Workflow Detail - Phase 10
 * View workflow details and run it
 */
const WorkflowDetail = () => {
  const { workflow_id } = useParams();
  const navigate = useNavigate();
  const { activeOrganization } = useOrganization();
  const [workflow, setWorkflow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPreview, setShowPreview] = useState(false);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    if (activeOrganization && workflow_id) {
      fetchWorkflow();
    }
  }, [activeOrganization, workflow_id]);

  const fetchWorkflow = async () => {
    try {
      setLoading(true);
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/workflows/${workflow_id}`,
        {},
        activeOrganization.organization_id
      );

      if (response.ok) {
        const data = await response.json();
        setWorkflow(data);
      }
    } catch (err) {
      console.error('Error fetching workflow:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRunWorkflow = async () => {
    try {
      setRunning(true);
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/workflows/run/${workflow_id}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ input_data: {}, dry_run: false })
        },
        activeOrganization.organization_id
      );

      if (response.ok) {
        const data = await response.json();
        // Navigate to run detail page
        navigate(`/partner-pro/workspace/workflow-runs/${data.run_id}`);
      } else {
        throw new Error('Failed to start workflow');
      }
    } catch (err) {
      console.error('Error running workflow:', err);
      alert('Failed to start workflow');
    } finally {
      setRunning(false);
      setShowPreview(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] p-6">
        <div className="max-w-4xl mx-auto">
          <div className="h-8 bg-gray-700 rounded w-48 mb-8 animate-pulse"></div>
          <div className="glass rounded-xl p-6 h-96 animate-pulse"></div>
        </div>
      </div>
    );
  }

  if (!workflow) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-6">
        <div className="glass rounded-2xl p-8 max-w-md text-center">
          <h2 className="text-2xl font-bold text-white mb-3">Workflow Not Found</h2>
          <button
            onClick={() => navigate('/partner-pro/workspace/workflows')}
            className="px-6 py-3 bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] text-white rounded-lg hover:opacity-90"
          >
            Back to Workflows
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] p-6">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate('/partner-pro/workspace/workflows')}
          className="flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft size={20} />
          Back to Workflows
        </button>

        {/* Workflow Header */}
        <div className="glass rounded-xl p-8 border border-[rgba(255,255,255,0.1)] mb-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-4xl">⚡</span>
                <div>
                  <h1 className="text-3xl font-bold text-white">{workflow.name}</h1>
                  <div className="flex items-center gap-3 mt-2">
                    <span className="px-3 py-1 bg-blue-500/20 text-blue-400 border border-blue-500/30 rounded-full text-sm">
                      {workflow.category}
                    </span>
                    <span className="px-3 py-1 bg-green-500/20 text-green-400 border border-green-500/30 rounded-full text-sm">
                      {workflow.status}
                    </span>
                  </div>
                </div>
              </div>
              <p className="text-gray-300 mb-4">{workflow.description}</p>
              <p className="text-sm text-gray-500">
                {workflow.steps?.length || 0} steps • {workflow.trigger_type} trigger
              </p>
            </div>

            <button
              onClick={() => setShowPreview(true)}
              className="px-6 py-3 bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] text-white rounded-lg hover:opacity-90 flex items-center gap-2 whitespace-nowrap"
            >
              <Play size={18} />
              Run Workflow
            </button>
          </div>
        </div>

        {/* Steps */}
        <div className="glass rounded-xl p-8 border border-[rgba(255,255,255,0.1)]">
          <h2 className="text-xl font-bold text-white mb-6">Workflow Steps</h2>
          <div className="space-y-4">
            {workflow.steps?.map((step, index) => (
              <div
                key={step.step_id}
                className="flex items-start gap-4 p-4 bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.1)] rounded-lg"
              >
                <div className="flex-shrink-0 w-8 h-8 bg-[#6366F1] rounded-full flex items-center justify-center text-white text-sm font-bold">
                  {index + 1}
                </div>
                <div className="flex-1">
                  <h4 className="text-white font-medium mb-1">{step.title}</h4>
                  <p className="text-sm text-gray-400">Type: {step.type}</p>
                  {step.requires_confirm && (
                    <span className="inline-block mt-2 text-xs px-2 py-1 bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 rounded">
                      Requires confirmation
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      <WorkflowPreviewModal
        workflow={workflow}
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        onConfirm={handleRunWorkflow}
        loading={running}
      />
    </div>
  );
};

export default WorkflowDetail;
